import { MultiStringField } from "@overmap-ai/forms"
import type { ReactNode } from "react"

import {
	NullableStringArrayEqualsFilterModifier,
	NullableStringArrayNotEqualsFilterModifier,
	StringArrayEveryFilterModifier,
	StringArrayNoneFilterModifier,
	StringArraySomeFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { MultiStringFieldFilterCell } from "./MultiStringFieldFilterCell"

type MultiStringFieldValue = string[] | null
type MultiStringFilterValue = string[] | null

export class MultiStringFieldFilterDef extends BaseFilterDef<MultiStringFieldValue, MultiStringFilterValue> {
	readonly id: string
	readonly defaultFilterValue: MultiStringFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly field: MultiStringField

	constructor(field: MultiStringField) {
		super()
		this.field = field
		this.id = field.id
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: NullableStringArrayEqualsFilterModifier,
			isValueValid: (_value: MultiStringFieldValue): _value is string[] | null => true,
			isFilterValueValid: (_filterValue: MultiStringFilterValue): _filterValue is string[] | null => true,
		}),
		notEquals: createFilterModifierConfig({
			modifier: NullableStringArrayNotEqualsFilterModifier,
			isValueValid: (_value: MultiStringFieldValue): _value is string[] | null => true,
			isFilterValueValid: (_filterValue: MultiStringFilterValue): _filterValue is string[] | null => true,
		}),
		every: createFilterModifierConfig({
			modifier: StringArrayEveryFilterModifier,
			isValueValid: (value: MultiStringFieldValue): value is string[] => Array.isArray(value),
			isFilterValueValid: (filterValue: MultiStringFilterValue): filterValue is string[] =>
				Array.isArray(filterValue),
		}),
		some: createFilterModifierConfig({
			modifier: StringArraySomeFilterModifier,
			isValueValid: (value: MultiStringFieldValue): value is string[] => Array.isArray(value),
			isFilterValueValid: (filterValue: MultiStringFilterValue): filterValue is string[] =>
				Array.isArray(filterValue),
		}),
		none: createFilterModifierConfig({
			modifier: StringArrayNoneFilterModifier,
			isValueValid: (value: MultiStringFieldValue): value is string[] => Array.isArray(value),
			isFilterValueValid: (filterValue: MultiStringFilterValue): filterValue is string[] =>
				Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<MultiStringFieldFilterDef>): ReactNode {
		return <MultiStringFieldFilterCell key={props.filter.id} field={this.field} {...props} />
	}
}
